import { CommonModule } from '@angular/common';
import { Component, computed, signal, Signal, viewChild } from '@angular/core';
import { CraftCalculatorStateService } from '../craft-calculator-state.service';
import { SectionComponent } from 'src/app/shared/components/section/section.component';
import { SubsectionComponent } from 'src/app/shared/components/subsection/subsection.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Item } from 'src/app/shared/models/items.model';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Location } from 'src/app/shared/models/location.model';
import { Craft } from 'src/app/shared/models/craft-models/craft-models';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { ItemImageComponent } from 'src/app/shared/components/item-image/item-image.component';

@Component({
  selector: 'app-craft-calculator-setup',
  imports: [
    CommonModule,
    SectionComponent,
    SubsectionComponent,
    MatFormFieldModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    FormsModule,
    MatCheckboxModule,
    CopyTextButtonComponent,
    CheckPriceButtonComponent,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatSortModule,
    MatInputModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    ItemImageComponent,
  ],
  templateUrl: './craft-calculator-setup.component.html',
  styleUrl: './craft-calculator-setup.component.scss',
})
export class CraftCalculatorSetupComponent {
  public Math = Math;
  public craftsSort: Signal<MatSort | undefined> = viewChild<MatSort>('craftsSort');
  public craftsPaginator: Signal<MatPaginator | undefined> = viewChild<MatPaginator>('craftsPaginator');
  public itemsFilterText = signal('');
  public searchCraftsText = signal('');
  public filteredCraftableItems = computed(() => {
    return this.craftCalculatorStateService
      .craftableItems()
      .filter((item) => {
        return item.localizedName.toLowerCase().includes(this.itemsFilterText().toLowerCase().trim());
      })
      .splice(0, 100);
  });
  public consumablesFilterText = signal('');
  public filteredConsumableItems = computed(() => {
    return this.craftCalculatorStateService
      .itemsSignal()
      .filter((item) => {
        return item.localizedName.toLowerCase().includes(this.consumablesFilterText().toLowerCase().trim()) && item.shopCategory === 'consumables' && (item.shopSubCategory === 'cooked' || item.shopSubCategory === 'potions');
      })
      .splice(0, 20);
  });

  public craftsDataSource: Signal<MatTableDataSource<Craft>> = computed(() => {
    const ds = this.setupCraftsDataSource();
    this.craftCalculatorStateService.oldCraftsSort = ds.sort;
    return ds;
  });
  public craftsDisplayedColumns: Signal<string[]> = computed(() => {
    let columns: string[] = [
      'product',
      'craftlocation',
      'consumables',
      'runs',
      'extraexpenses',
      'weight',
      'ignorejournals',
      'usefocus',
      'rrr',
      'craftfee',
      'expenses',
      'totalfame',
      'famepersilver',
      'fameperfocus',
      'totalfocus',
      'profitperfocus',
      'selected',
      'totalcraftfee',
      'result',
      'resultpercentage',
    ];

    if (this.craftCalculatorStateService.craftGroup().hideConsumablesPicker()) {
      columns = columns.filter((col) => col !== 'consumables');
    }

    if (this.craftCalculatorStateService.craftGroup().hideFocusInfo()) {
      columns = columns.filter((col) => !['usefocus', 'fameperfocus', 'totalfocus', 'profitperfocus'].includes(col));
    }

    if (this.craftCalculatorStateService.craftGroup().hideFameInfo()) {
      columns = columns.filter((col) => !['totalfame', 'famepersilver'].includes(col));
    }

    return columns;
  });
  constructor(
    public craftCalculatorStateService: CraftCalculatorStateService,
    private matDialog: MatDialog
  ) {}

  trackByCraftId(index: number, craft: any): string {
    return craft.product.uniqueName;
  }

  private setupCraftsDataSource(): MatTableDataSource<Craft> {
    let crafts = this.craftCalculatorStateService.craftGroup().crafts();

    if (this.searchCraftsText().trim() !== '') {
      const searchText = this.searchCraftsText().toLowerCase().trim();
      crafts = crafts.filter((craft) => {
        return craft.product.localizedName.toLowerCase().includes(searchText);
      });
    }

    if (this.craftCalculatorStateService.craftsSelectedTiers().length > 0) {
      crafts = crafts.filter((craft) => this.craftCalculatorStateService.craftsSelectedTiers().includes(craft.product.tier ?? 0));
    }

    if (this.craftCalculatorStateService.craftsSelectedEnchantments().length > 0) {
      crafts = crafts.filter((craft) => this.craftCalculatorStateService.craftsSelectedEnchantments().includes(craft.product.enchantmentLevel ?? 0));
    }

    if (this.craftCalculatorStateService.craftGroup().hideMissingPrices()) {
      crafts = crafts.filter((craft) => !craft.hasMissingPrices());
    }

    if (this.craftCalculatorStateService.craftGroup().hideUnselectedCrafts()) {
      crafts = crafts.filter((craft) => craft.selected());
    }

    if (this.craftCalculatorStateService.craftGroup().onlyShowMainRecipes()) {
      crafts = crafts.filter((craft) => craft.isMainRecipe);
    }

    if (this.craftCalculatorStateService.craftGroup().onlyMissingPricesCrafts()) {
      crafts = crafts.filter((craft) => craft.hasMissingPrices());
    }

    const ds = new MatTableDataSource<Craft>(crafts);

    const craftSort = this.craftsSort();

    if (craftSort) {
      ds.sortingDataAccessor = (item: Craft, property) => {
        const defaultSort = item.product.groupUniqueName.toLowerCase() + '_' + item.product.tier + '_' + item.product.enchantmentLevel;
        switch (property) {
          case 'product':
            return defaultSort;
          case 'craftlocation':
            return item.craftLocation()?.displayName.toLowerCase() + '_' + defaultSort;
          case 'runs':
            return item.numberOfRuns();
          case 'rrr':
            return item.rrr();
          case 'selected':
            return (item.selected() ? '1_' : '0_') + defaultSort;
          case 'result':
            return item.result();
          case 'resultpercentage':
            return item.resultPercent();
          case 'expenses':
            return item.totalExpenses();
          case 'totalfame':
            return item.craftFamePlayerTotal();
          case 'famepersilver':
            return item.craftFamePlayerPerExpenses();
          case 'profitperfocus':
            return item.resultPerFocus();
          case 'ignorejournals':
            return (item.ignoreJournals() ? '1_' : '0_') + defaultSort;
          case 'usefocus':
            return (item.useFocus() ? '1_' : '0_') + defaultSort;
          case 'totalfocus':
            return item.totalFocus();
          case 'craftfee':
            return item.craftFeePerNutrition();
          case 'totalcraftfee':
            return item.totalCraftFee();
          default:
            return defaultSort;
        }
      };

      ds.sort = craftSort;

      if (this.craftCalculatorStateService.oldCraftsSort) {
        if (ds.sort) {
          ds.sort.active = this.craftCalculatorStateService.oldCraftsSort?.active;
          ds.sort.direction = this.craftCalculatorStateService.oldCraftsSort?.direction;
        }
      }
    }

    const craftPaginator = this.craftsPaginator();

    if (craftPaginator) {
      ds.paginator = craftPaginator;
    }
    return ds;
  }

  public generateMissingPricesText(missingPricesName: string[]): string {
    if (missingPricesName.length > 0) {
      const listPrefix = '\n- ';
      return 'Missing prices for:' + listPrefix + missingPricesName.join(listPrefix);
    } else {
      return '';
    }
  }

  displayConsumableName(item: Item): string {
    return '';
  }

  resetCraftGroup(): void {
    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Reset', message: `Are you sure you want to reset the craft group? This will remove all settings and any unsaved data for the current craft group will be lost.` },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.craftCalculatorStateService.resetCraftGroup();
      }
    });
  }

  onRrrInput(event: Event, craft: any) {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (value === '' || value === null || value === undefined) {
      craft.rrr.set(null);
    } else {
      const percentageValue = parseFloat(value);
      // Convert percentage to decimal and round to 4 decimal places to avoid precision issues
      const decimalValue = Math.round((percentageValue / 100) * 10000) / 10000;
      craft.rrr.set(decimalValue);
    }
  }

  // MARK: propagation methods
  propagateConsumables(sourceCraft: Craft, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .filter((craft) => craft != sourceCraft)
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.consumables().forEach((consumable) => {
            craft.removeConsumable(consumable.inputItem.item);
          });
          sourceCraft.consumables().forEach((consumable) => {
            craft.addConsumable(consumable.inputItem.item, consumable.inputAmountPerRun());
          });
        }
      });
  }
  propagateRuns(runs: number, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.numberOfRuns.set(runs);
        }
      });
  }

  propagateRRR(rrr: number, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.rrr.set(rrr);
        }
      });
  }

  propagateCraftLocation(craftLocation: Location, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.craftLocation.set(craftLocation);
        }
      });
  }

  propagateUseFocus(useFocus: boolean, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.useFocus.set(useFocus);
        }
      });
  }

  propagateIgnoreJournals(ignoreJournals: boolean, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.ignoreJournals.set(ignoreJournals);
        }
      });
  }

  propagateCraftFee(craftFee: number, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.craftFeePerNutrition.set(craftFee);
        }
      });
  }

  propagateExtraExpenses(extraExpenses: number, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.extraExpenses.set(extraExpenses);
        }
      });
  }

  propagateSelected(selected: boolean, groupUniqueName?: string): void {
    this.craftCalculatorStateService
      .craftGroup()
      .crafts()
      .forEach((craft) => {
        if (!groupUniqueName || craft.product.groupUniqueName === groupUniqueName) {
          craft.selected.set(selected);
        }
      });
  }
}
